import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search as SearchIcon, Loader2, ShieldAlert, UserCheck, Newspaper, Coins, ArrowUpRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const LEVEL_META = {
  HIGH: { cls: "bg-red-950/70 text-red-300 border-red-700", dot: "bg-red-500", ring: "ring-red-500/30" },
  MEDIUM: { cls: "bg-amber-950/70 text-amber-300 border-amber-700", dot: "bg-amber-500", ring: "ring-amber-500/30" },
  LOW: { cls: "bg-emerald-950/70 text-emerald-300 border-emerald-700", dot: "bg-emerald-500", ring: "ring-emerald-500/30" },
};

function Flag({ icon: Icon, label, value, danger }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2.5">
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <Icon className={`h-4 w-4 ${danger ? "text-red-400" : "text-slate-500"}`} />
        {label}
      </div>
      <span className={`text-sm font-semibold ${danger ? "text-red-300" : "text-slate-200"}`}>{value}</span>
    </div>
  );
}

export default function Search() {
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("");
  const [type, setType] = useState("Individual");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error("Enter a name, company or wallet address");
      return;
    }
    setLoading(true);
    setRes(null);
    try {
      const { data } = await api.post("/search", { query, country, type });
      setRes(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const input =
    "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-blue-500";
  const lvl = res ? LEVEL_META[res.risk_level] || LEVEL_META.LOW : null;

  return (
    <div className="max-w-3xl space-y-6" data-testid="search-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
          Entity Search
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Instantly screen a name, company or wallet against sanctions, PEP and adverse-media sources.
        </p>
      </div>

      <form onSubmit={run} className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="search-query-input"
            className={`${input} w-full pl-11 text-base`}
            placeholder="e.g. Tornado Cash, Banco Nacional de Cuba, 0x8589…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-3">
          <input
            data-testid="search-country-input"
            className={`${input} flex-1 min-w-[160px]`}
            placeholder="Country (optional)"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="search-type-select" className={`${input} w-44`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["Individual", "Company", "Exchange", "VASP", "Protocol", "Wallet"].map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            data-testid="search-submit-btn"
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <SearchIcon className="h-4 w-4" />}
            Search
          </button>
        </div>
      </form>

      {res && (
        <div className={`rounded-xl border bg-slate-900/60 p-6 ring-1 ${lvl.ring}`} data-testid="search-result">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-heading text-xl font-bold text-slate-100">{res.search_query}</div>
              <div className="mt-1 font-mono text-xs uppercase tracking-wide text-slate-500">
                {res.entity_type} · {res.jurisdiction || "—"}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span
                data-testid="risk-level-badge"
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 font-heading text-sm font-extrabold tracking-wide ${lvl.cls}`}
              >
                <span className={`h-2.5 w-2.5 rounded-full ${lvl.dot}`} />
                {res.risk_level} RISK
              </span>
              <span className="font-mono text-xs text-slate-500">
                score {res.risk_score}/10 · {res.recommendation}
              </span>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Flag icon={ShieldAlert} label="Sanctioned"
              value={res.flags.is_sanctioned ? res.flags.sanction_source : "No"} danger={res.flags.is_sanctioned} />
            <Flag icon={UserCheck} label="PEP"
              value={res.flags.is_pep ? res.flags.pep_type : "No"} danger={res.flags.is_pep} />
            <Flag icon={Newspaper} label="Adverse media"
              value={res.flags.adverse_media_found ? "Found" : "None"} danger={res.flags.adverse_media_found} />
            <Flag icon={Coins} label="Crypto risk"
              value={res.flags.crypto_risk} danger={res.flags.crypto_risk !== "None"} />
          </div>

          {res.flags.adverse_media_summary && (
            <p className="mt-4 text-sm text-slate-300">
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Adverse media: </span>
              {res.flags.adverse_media_summary}
            </p>
          )}

          <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/50 p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Admin notes</div>
            <p className="mt-1 text-sm text-slate-300" data-testid="search-admin-notes">{res.admin_notes}</p>
          </div>

          <Link
            to={`/cases/${res.screening_id}`}
            data-testid="search-view-case"
            className="mt-4 inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
          >
            View full case &amp; audit record <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}
